import React, { useState } from 'react';
import { HiOutlineTrash } from 'react-icons/hi';
import TransactionDetailModal from './TransactionDetailModal';
import { formatApiDateTime } from '../../../lib/helpers';
import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import { cantidadDespachada, mermaDeLinea, notaPedidoMerma, esPeso } from '../../../lib/helpers/products';

function TransactionsBox({ transactions, onReverse }) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };
  const validTransactions = transactions ? transactions.filter(t => t.status !== 'reversed') : [];

  if (!validTransactions || validTransactions.length === 0) {
    return (
      <div className="bg-white p-8 rounded-lg shadow border border-gray-200 text-center text-gray-500">
        No hay transacciones para mostrar
      </div>
    );
  }

  const formatDate = (dateString) =>
    formatApiDateTime(dateString, { fallback: '-' });

  const getTypeColor = (type) => {
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'bg-green-100 text-green-800';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'bg-purple-100 text-purple-800';
    if (t === 'refund' || t === 'reembolso') return 'bg-red-100 text-red-800';
    // (la cuota usa el tono de refund; su etiqueta ya la distingue)
    return 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type, concept) => {
    // La cuota de inscripción viaja como REFUND pero no es dinero devuelto.
    if (concept === 'membership_fee') return 'Cuota de inscripción';
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'Venta';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'Aportación';
    if (t === 'refund' || t === 'reembolso') return 'Reembolso';
    return type || '-';
  };

  const isVenta = (type) => {
    const t = type?.toLowerCase();
    return t === 'purchase' || t === 'sale' || t === 'venta';
  };

  // Reversible = venta, aportación o cuota de inscripción (cada una tiene su
  // endpoint; el handler de la página elige). Un reembolso ya es la anulación
  // de otra cosa, así que no se re-anula.
  const isReversible = (tx) => {
    const t = String(tx.type || '').toLowerCase();
    if (tx.concept === 'membership_fee') return true;
    return isVenta(t) || ['credit', 'credito', 'crédito', 'add_cash'].includes(t);
  };

  const reverseLabel = (tx) => {
    if (tx.concept === 'membership_fee') return 'Anular cuota';
    return isVenta(tx.type) ? 'Anular venta' : 'Anular aportación';
  };

  const handleReverseClick = (e, transaction) => {
    e.stopPropagation();
    if (onReverse) onReverse(transaction);
  };

  return (
    <>
      {/* Modal de detalles */}
      <TransactionDetailModal
        active={isModalOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
        onReverse={onReverse}
      />

      {/* Vista de tarjetas para móvil */}
      <div className="block md:hidden space-y-3">
        {validTransactions.map((transaction) => (
          <div
            key={transaction.id}
            className="bg-white rounded-lg shadow border border-gray-200 p-3 hover:shadow-md transition-shadow"
          >
            <div
              className="flex justify-between items-start mb-2 cursor-pointer"
              onClick={() => handleTransactionClick(transaction)}
            >
              <div className="flex-1">
                <div className="text-xs text-gray-500">ID: #{transaction.id?.toString().slice(0, 8) || '-'}</div>
                <div className="font-medium text-sm mt-1">
                  {transaction.user_name || transaction.user_email || `Usuario #${transaction.user_id?.toString().slice(0, 8)}` || '-'}
                </div>
                {transaction.user_name && transaction.user_email && (
                  <div className="text-xs text-gray-500">{transaction.user_email}</div>
                )}
              </div>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                {getTypeLabel(transaction.type, transaction.concept)}
              </span>
            </div>

            <div
              className="grid grid-cols-2 gap-2 text-sm mt-3 cursor-pointer"
              onClick={() => handleTransactionClick(transaction)}
            >
              <div>
                <span className="text-gray-500 text-xs">Monto:</span>
                <div className="font-semibold">€{parseFloat(transaction.amount || 0).toFixed(2)}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Fecha:</span>
                <div className="text-xs">{formatDate(transaction.timestamp || transaction.created_at)}</div>
              </div>
            </div>

            {transaction.items && transaction.items.length > 0 && (
              <details className="mt-3 cursor-pointer">
                <summary
                  className="text-blue-600 hover:text-blue-800 text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  {transaction.items.length} producto{transaction.items.length !== 1 ? 's' : ''}
                </summary>
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                  {transaction.items.map((item, index) => (
                    <div key={index} className="border-b border-gray-200 pb-1 last:border-b-0">
                      <div className="font-medium">{item.product_name || `Producto #${item.product_id}`}</div>
                      <div className="text-gray-600">
                        Cantidad: {cantidadDespachada(item)}{(esPeso(item) || mermaDeLinea(item) != null) ? ' g' : ''}
                        {notaPedidoMerma(item) ? ` (${notaPedidoMerma(item)})` : ''} · €{parseFloat(item.unit_price || 0).toFixed(2)} →
                        €{(item.quantity * parseFloat(item.unit_price || 0)).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {isReversible(transaction) && onReverse && (
              <button
                type="button"
                onClick={(e) => handleReverseClick(e, transaction)}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700"
              >
                <HiOutlineTrash className="w-4 h-4" /> {reverseLabel(transaction)}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Vista de tabla para desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <TableHead>
              <Th>ID</Th>
              <Th>Usuario</Th>
              <Th>Tipo</Th>
              <Th>Monto</Th>
              <Th>Fecha</Th>
              <Th>Productos</Th>
              <Th align="right">Acciones</Th>
            </TableHead>
            <tbody>
              {validTransactions.map((transaction) => (
                // Sin cebra: la línea de separación de la fila ya distingue una de
                // otra, y encima la cebra peleaba con el hover (dos fondos grises).
                //
                // El clic sigue celda a celda a propósito, no en la fila: «Productos»
                // despliega su propio detalle y «Acciones» tiene el botón de revertir.
                // Subirlo a la fila abriría el modal al desplegar los productos.
                <Tr key={transaction.id}>
                  <Td
                    className="cursor-pointer px-4 py-3 font-mono text-sm"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    #{transaction.id?.toString().slice(0, 8) || '-'}
                  </Td>
                  <Td
                    className="cursor-pointer px-4 py-3"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    <div className="text-sm font-medium">
                      {transaction.user_name || transaction.user_email || `Usuario #${transaction.user_id?.toString().slice(0, 8)}` || '-'}
                    </div>
                    {transaction.user_name && transaction.user_email && (
                      <div className="text-xs text-[var(--muted-foreground)]">{transaction.user_email}</div>
                    )}
                  </Td>
                  <Td
                    className="cursor-pointer px-4 py-3"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    {/* Los tonos del tipo (venta / aportación / reembolso) se quedan
                        como están: son una escala categórica y el sistema todavía no
                        tiene componente de insignia. Recolorearla a ojo aquí sería
                        inventarse la escala en una sola pantalla. */}
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                      {getTypeLabel(transaction.type, transaction.concept)}
                    </span>
                  </Td>
                  <Td
                    className="cursor-pointer px-4 py-3 font-semibold"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    €{parseFloat(transaction.amount || 0).toFixed(2)}
                  </Td>
                  <Td
                    className="cursor-pointer px-4 py-3 text-sm"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    {formatDate(transaction.timestamp || transaction.created_at)}
                  </Td>
                  <Td className="px-4 py-3">
                    {transaction.items && transaction.items.length > 0 ? (
                      <details className="cursor-pointer">
                        <summary className="text-sm text-[var(--primary)] hover:text-[var(--primary-strong)]">
                          {transaction.items.length} producto{transaction.items.length !== 1 ? 's' : ''}
                        </summary>
                        <div className="mt-2 space-y-1 rounded-sm bg-[var(--muted)] p-2 text-xs">
                          {transaction.items.map((item, index) => (
                            <div key={index} className="border-b border-[var(--border)] pb-1 last:border-b-0">
                              <div className="font-medium">{item.product_name || `Producto #${item.product_id}`}</div>
                              <div className="text-[var(--muted-foreground)]">
                                Cantidad: {cantidadDespachada(item)}{(esPeso(item) || mermaDeLinea(item) != null) ? ' g' : ''}
                                {notaPedidoMerma(item) ? ` (${notaPedidoMerma(item)})` : ''} · €{parseFloat(item.unit_price || 0).toFixed(2)} →
                                €{(item.quantity * parseFloat(item.unit_price || 0)).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <span className="text-sm text-[var(--muted-foreground)]">Sin items</span>
                    )}
                  </Td>
                  <Td align="right" className="px-4 py-3">
                    {isReversible(transaction) && onReverse && (
                      <button
                        type="button"
                        title={reverseLabel(transaction)}
                        onClick={(e) => handleReverseClick(e, transaction)}
                        className="rounded-sm p-1.5 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--destructive-bg)] hover:text-[var(--destructive)]"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default TransactionsBox;
